import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Clock, Calendar, Users } from 'lucide-react';
import { courses } from '../mockData';

const CoursesHub = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              International Dental Licensing Courses
            </h1>
            <p className="text-lg md:text-xl text-gray-100">
              World-class training programs designed to help you excel in your international dental licensing examinations
            </p>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {courses.map((course) => (
              <Card key={course.id} className="hover:shadow-xl transition-all border-2 hover:border-[#1e3a8a]">
                <CardHeader>
                  {course.popular && (
                    <div className="inline-block px-4 py-1 bg-[#1e3a8a] text-white text-sm font-semibold rounded-full mb-3 w-fit">
                      Most Popular
                    </div>
                  )}
                  <CardTitle className="text-2xl md:text-3xl">{course.title}</CardTitle>
                  <CardDescription className="text-base mt-2">{course.shortDesc}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-gray-600">
                      <Clock className="h-5 w-5 text-[#1e3a8a]" />
                      <span><span className="font-semibold">Duration:</span> {course.duration}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <Calendar className="h-5 w-5 text-[#1e3a8a]" />
                      <span><span className="font-semibold">Mode:</span> {course.mode}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <Users className="h-5 w-5 text-[#1e3a8a]" />
                      <span><span className="font-semibold">Schedule:</span> Mon-Fri (10 AM - 7 PM), Sat (10 AM - 2 PM)</span>
                    </div>
                  </div>
                  <Button asChild className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90">
                    <Link to={`/courses/${course.id}`}>
                      View Course Details <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Our Training Different */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Makes Our Training Different
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">01</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Simulation-Based Learning
              </h3>
              <p className="text-gray-600">
                Real exam environment with authentic equipment, time constraints, and assessment criteria
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">02</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Rubric-Based Teaching
              </h3>
              <p className="text-gray-600">
                Training aligned with actual examination rubrics to ensure you meet every assessment criterion
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">03</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Continuous Assessment
              </h3>
              <p className="text-gray-600">
                3-5 comprehensive mock exams with detailed feedback to track your progress and improvement
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Begin Your Preparation?
          </h2>
          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your goals and find the right course for you
          </p>
          <Button asChild size="lg" variant="secondary" className="bg-white text-[#1e3a8a] hover:bg-gray-100">
            <Link to="/contact">Enquire Now</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CoursesHub;
